import React from 'react';

import { DAYTIME_SERVICES } from '@rover/constants/src/translations/service.constants';
import { Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

const CIRCLE_SIZE = 32;
const ICON_SIZE = 24;

type Props = {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  testID?: string;
  serviceSlug?: string;
  backgroundColor?: string;
  iconColor?: string;
  isAway?: boolean;
};

const ServiceIconBadge = ({
  icon: IconComponent,
  testID,
  serviceSlug,
  backgroundColor,
  iconColor,
  isAway = false,
}: Props): JSX.Element => {
  const theme = useTheme();
  // Daytime services carry the orange palette; everything else stays on the blue one. An
  // away service drops both for a neutral one, so the icon is dimmed alongside the name it
  // sits next to rather than staying lit on a greyed-out row.
  const isDaytime = (DAYTIME_SERVICES as string[]).includes(serviceSlug ?? '');
  const palette = {
    background: isDaytime ? theme.colors.orange[200] : theme.colors.blue[200],
    icon: isDaytime ? theme.colors.orange[700] : theme.colors.blue[800],
  };
  // The neutral steps this reaches for are the same values as `background.tertiary` and
  // `text.tertiary`, which is the grey the away service name is dimmed to; the palettes are
  // what this component has access to here.
  const defaultBackgroundColor = isAway ? theme.colors.neutral[200] : palette.background;
  const defaultIconColor = isAway ? theme.colors.neutral[700] : palette.icon;

  return (
    <Flex
      data-testid={testID}
      flexShrink="0"
      alignItems="center"
      justifyContent="center"
      borderRadius="50%"
      width={CIRCLE_SIZE}
      height={CIRCLE_SIZE}
      backgroundColor={backgroundColor ?? defaultBackgroundColor.toString()}
      aria-hidden="true"
    >
      <IconComponent
        width={ICON_SIZE}
        height={ICON_SIZE}
        fill={iconColor ?? defaultIconColor.toString()}
        aria-hidden="true"
      />
    </Flex>
  );
};

export default ServiceIconBadge;
